import { useCallback, useEffect, useReducer, useState } from 'react';

import axios, { type AxiosError } from 'axios';
import { createContainer } from 'unstated-next';

import { getDemoBreakoutImages } from '../components/allBreakoutRooms/demo-images';
import { useOrgSettings } from '../hooks/useOrgSettings';
import { getUrls } from '../utils/url';

interface AllBreakoutRoomsState {
    token?: string;
    isDemo: boolean;
    zoomId?: string;
    userId?: string;
    classId?: string;
    meetingId?: string;
    participantId?: string;
    breakoutNames: string[];
}

export interface BreakoutImages {
    [key: string]: {
        breakout_name: string;
        url: string;
    }
}

interface BreakoutImageState {
    loading: boolean,
    error?: Error,
    images: BreakoutImages
}

export interface AllBreakoutRoomsProps extends AllBreakoutRoomsState {}

const fallback = {
    token: undefined,
    isDemo: false,
    zoomId: undefined,
    userId: undefined,
    classId: undefined,
    meetingId: undefined,
    participantId: undefined,
    breakoutNames: []
};

type Action =
    | { type: 'demo' }
    | { type: 'init', payload: BreakoutImages }
    | { type: 'init-error', payload: Error }
    | { type: 'update-error', payload: Error }
    | { type: 'update-images', payload: BreakoutImages };

const reducer = (state: BreakoutImageState, action: Action): BreakoutImageState => {
    switch (action.type) {
        case 'init': return {
            loading: false,
            error: undefined,
            images: action.payload
        };
        case 'demo': return {
            loading: false,
            error: undefined,
            images: getDemoBreakoutImages()
        };
        case 'init-error': return {
            loading: false,
            error: action.payload,
            images: {}
        };
        case 'update-error': return {
            ...state,
            error: action.payload
        };
        case 'update-images': return {
            ...state,
            images: action.payload
        };
        default: return state;
    }
};

const useAllBreakoutRooms = (initialState: AllBreakoutRoomsProps = fallback) => {
    const { loading, error, data: classSettings } = useOrgSettings();

    const imageState: BreakoutImageState = { loading: true, error: undefined, images: {} };

    const [images, dispatch] = useReducer(reducer, imageState);
    const [state, setState] = useState<AllBreakoutRoomsState>(initialState);
    const [roomCount, setRoomCount] = useState(state.breakoutNames.length);
    const [perPage, setPerPage] = useState(Math.min(state.breakoutNames.length, 6));
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [countCurrentPage, setCountCurrentPage] = useState(perPage);

    useEffect(() => {
        if (!initialState) return;

        setState(initialState);
    }, [initialState]);

    useEffect(() => {
        const count = state.breakoutNames.slice((currentPage - 1) * perPage, currentPage * perPage).length;
        setCountCurrentPage(count);
    }, [currentPage, perPage, state.breakoutNames]);

    useEffect(() => {
        if (roomCount / perPage > 0) {
            setTotalPages(Math.ceil(roomCount / perPage));
        } else {
            setTotalPages(1);
        }
    }, [perPage, roomCount, setTotalPages, state.breakoutNames.length]);

    const fetchBreakoutImages = useCallback(() => {
        const { api } = getUrls();

        const body = { breakout_names: state.breakoutNames };
        const headers = { authorization: `Bearer ${state.token}` };

        return axios.post(`${api}/v1/breakout/images`, body, { headers });
    }, [state.breakoutNames, state.token]);

    useEffect(() => {
        const fetchBreakoutData = async () => {
            try {
                const result = await fetchBreakoutImages();

                dispatch({ type: 'init', payload: result.data.urls as BreakoutImages });
            } catch (err) {
                console.error(err);

                dispatch({ type: 'init-error', payload: err as AxiosError });
            }
        };

        if (state.isDemo) {
            dispatch({ type: 'demo' });
        } else {
            fetchBreakoutData();
        }
    }, [fetchBreakoutImages, state]);

    useEffect(() => {
        /**
         * Re-fetch image urls with new signatures to
         * force re-download updated images
         */
        const id = setInterval(async () => {
            if (state.isDemo) return;

            try {
                const result = await fetchBreakoutImages();
                dispatch({ type: 'update-images', payload: result.data.urls as BreakoutImages });
            } catch (err) {
                console.error(err);
                dispatch({ type: 'update-error', payload: err as AxiosError });
            }
        }, 3000);

        return () => clearInterval(id);
    }, [fetchBreakoutImages, state]);

    return {
        ...state,
        classSettings,
        roomCount,
        setRoomCount,
        perPage,
        setPerPage,
        currentPage,
        setCurrentPage,
        totalPages,
        setTotalPages,
        imageData: images,
        loading,
        countCurrentPage,
        error
    };
};

const AllBreakoutRoomsContainer = createContainer(useAllBreakoutRooms);

export default AllBreakoutRoomsContainer;
